import { Component } from '@verndale/core';

import breakpoints from '../utils/breakpoints';
import { fontsLoaded } from '../utils/typography';
import { nextFrameAsync } from '../utils/timedEvents';
import focusableElements from '../utils/focusable-elements';

const MAX_TEXT_HEIGHT = 402;
const MAX_TEXT_HEIGHT_MOBILE = 300;
const SCROLL_MARGIN = 64;
const FADE_HEIGHT = 120;

class ReadMore extends Component {
  setupDefaults() {
    this.dom = {
      $text: this.el._$('.read-more__text'),
      $buttons: this.el._$$('.read-more__button'),
      $moreButton: this.el._$('.read-more__button--more'),
      $lessButton: this.el._$('.read-more__button--less'),
      $fade: this.el._$('.read-more__fade')
    };

    this.focusableElements = this.dom.$text._$$(focusableElements);

    this.textHidden = false;
    this.collapsed = false;
    this.lastKnownXSize = window.innerWidth;
    this.init();
  }

  init() {
    this.dom.$text.style.maxHeight = null;
    fontsLoaded(::this.toggleView);
  }

  addListeners() {
    const { $buttons } = this.dom;

    $buttons._on('click', () => this.toggleView(true));
    window._on('resize', ::this.handleResize);
  }

  async handleResize() {
    if (this.lastKnownXSize !== window.innerWidth) {
      await nextFrameAsync();
      const { $text } = this.dom;
      $text.style.maxHeight = null;
      this.el.fullHeight = $text.offsetHeight;
      this.collapsed = true;
      this.toggleView();
    }
  }

  toggleView(scroll = false) {
    const { $text, $fade, $moreButton, $lessButton } = this.dom;

    if ($text.clientHeight > this.getMaxHeight()) {
      this.hideText(scroll);
    } else if (this.textHidden) {
      this.showText();
    } else {
      $fade.style.display = 'none';
      $moreButton.style.display = 'none';
      $lessButton.style.display = 'none';
    }
  }

  hideText(scroll) {
    const { $text, $fade } = this.dom;

    this.el.fullHeight = $text.offsetHeight;
    $fade.style.display = 'block';
    $text.style.maxHeight = `${this.getMaxHeight()}px`;
    $fade.style.opacity = 1;
    $fade.style.visibility = 'visible';
    this.showMoreButton();
    this.textHidden = true;

    if (this.collapsed && scroll) {
      const { top } = this.el.getBoundingClientRect();

      window.scroll({
        top: top + window.scrollY - SCROLL_MARGIN,
        behavior: 'smooth'
      });
    }

    this.focusableElements.forEach((el) => {
      const containerTop = this.dom.$text.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const maxHeight = this.getMaxHeight();

      el.tabIndex = elTop - containerTop > maxHeight - FADE_HEIGHT ? -1 : 0;
    });

    this.collapsed = true;
  }

  showText() {
    const { $text, $fade } = this.dom;

    if (!this.el.fullHeight) return;

    $fade.style.display = 'block';
    $text.style.maxHeight = `${this.el.fullHeight}px`;
    $fade.style.opacity = 0;
    $fade.style.visibility = 'hidden';
    this.showLessButton();
    this.focusableElements.forEach((el) => (el.tabIndex = 0));
    this.textHidden = false;
  }

  showMoreButton() {
    const { $moreButton, $lessButton } = this.dom;

    $moreButton.style.display = 'block';
    $lessButton.style.display = 'none';
  }

  showLessButton() {
    const { $moreButton, $lessButton } = this.dom;

    $lessButton.style.display = 'block';
    $moreButton.style.display = 'none';
  }

  getMaxHeight() {
    return breakpoints.isLg() ? MAX_TEXT_HEIGHT : MAX_TEXT_HEIGHT_MOBILE;
  }
}

export default ReadMore;
